import HttpClient from 'gateway/HttpClient';
import axios, { AxiosHeaders, AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken, InternalAxiosRequestConfig } from 'axios';
import HttpMethod from 'constant/HttpMethod';
import { apiUrl } from '../config/binding/InstanceBinding';
import MessageUtil from '../util/MessageUtil';
import DeployErrorUtil from '../util/DeployErrorUtil';

function serializeParams(params: Record<string, any>): string {
  if (!params || typeof params !== 'object') return '';
  const parts: string[] = [];
  const append = (key: string, value: any): void => {
    if (value === null || typeof value === 'undefined') return;
    if (Array.isArray(value)) {
      value.forEach((v) => append(key, v));
      return;
    }
    let encoded: string;
    if (value instanceof Date) {
      encoded = value.toISOString();
    } else if (typeof value === 'object') {
      encoded = JSON.stringify(value);
    } else {
      encoded = String(value);
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(encoded)}`);
  };
  Object.keys(params).forEach((key) => append(key, params[key]));
  return parts.join('&');
}

function buildSafeHeaders(input?: Record<string, any>): Record<string, any> {
  const safe: Record<string, any> = Object.create(null);
  if (!input || typeof input !== 'object') return safe;
  for (const key of Object.keys(input)) {
    if (key === '__proto__' || key === 'prototype' || key === 'constructor') continue;
    const value = (input as Record<string, any>)[key];
    if (typeof value === 'string') {
      safe[key] = value.replace(/[\r\n]+/g, '');
    } else {
      safe[key] = value;
    }
  }
  return safe;
}

class ApiClient implements HttpClient {
  private readonly httpClient: AxiosInstance;

  constructor(tenant: string) {
    this.httpClient = axios.create({
      baseURL: apiUrl,
      headers: {
        version: 'v1',
        'tenant-id': tenant,
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      paramsSerializer: { serialize: serializeParams },
    });
    this.httpClient.interceptors.request.use((request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      //console.time("Request " + request.url);
      return request;
    });
    this.httpClient.interceptors.response.use(async (response: AxiosResponse) => {
      //console.timeEnd("Request " + response.config.url);
      if (response.status == 202) {
        setTimeout(() => {
          MessageUtil.success({
            summary: response.data.message,
          });
          DeployErrorUtil.showApprovalWarning(response.data);
        }, 0);
      }
      return response;
    });
  }

  public async execute<T>(
    httpMethod: HttpMethod,
    endpoint: string,
    data: any,
    queryParams?: Record<string, any>,
    headers?: Record<string, any>,
    uploadEvent?: (progressEvent: ProgressEvent) => void,
    token?: CancelToken,
  ): Promise<T> {
    const requestHeaders = buildSafeHeaders(headers);
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      requestHeaders['Content-Type'] = null;
    }
    const request: AxiosRequestConfig = {
      url: endpoint,
      method: httpMethod,
      headers: requestHeaders,
      data,
      params: queryParams,
      onUploadProgress: uploadEvent,
      cancelToken: token,
    };
    try {
      const response = await this.httpClient.request(request);
      return response.data;
    } catch (e: any) {
      return Promise.reject(e);
    }
  }

  public executeRaw<T>(
    httpMethod: HttpMethod,
    endpoint: string,
    data?: Record<string, any> | Array<any> | undefined,
    queryParams?: Record<string, any>,
    headers?: Record<string, any>,
  ): Promise<T> {
    const request: AxiosRequestConfig = {
      url: endpoint,
      method: httpMethod,
      headers: buildSafeHeaders(headers),
      data,
      params: queryParams,
    };
    return this.httpClient.request(request);
  }

  public executeWithRequest<T>(request: AxiosRequestConfig): Promise<T> {
    return this.httpClient.request(request);
  }
}
export { AxiosHeaders };

export default ApiClient;
